import {
  checkboxClasses,
  default as MuiCheckbox,
  type CheckboxProps as MuiCheckboxProps,
} from '@mui/material/Checkbox';
import {
  formControlLabelClasses,
  default as MuiFormControlLabel,
  type FormControlLabelProps,
} from '@mui/material/FormControlLabel';
import { type Theme, useTheme } from '../../../../theme';
import {
  CheckboxDefault,
  CheckboxChecked,
  CheckboxHasError,
  CheckboxIndeterminate,
} from './variants';
import type { MouseEvent, ReactNode, JSX } from 'react';
import Box from '@mui/material/Box';
import { BodyS } from '../../../../typography';
import { AriaProps } from '../../../../types';

export type CheckboxProps = AriaProps &
  Pick<
    MuiCheckboxProps,
    | 'disabled'
    | 'inputRef'
    | 'inputProps'
    | 'onChange'
    | 'checked'
    | 'indeterminate'
  > & {
    label?: FormControlLabelProps['label'];
    className?: string;
    tabIndex?: number;
    labelSx?: FormControlLabelProps['sx'];
    checkboxSx?: MuiCheckboxProps['sx'];
    hasError?: boolean;
    hasBackground?: boolean;
  };

const handleClick = (event: MouseEvent): void => {
  const target = event.currentTarget as HTMLElement;
  target.blur();
};

type CheckboxWrapperProps = Pick<
  CheckboxProps,
  'hasBackground' | 'disabled' | 'hasError'
> & {
  theme: Theme;
  children: ReactNode;
};

const CheckboxWrapper = ({
  children,
  theme,
  hasBackground = false,
  disabled = false,
  hasError = false,
}: CheckboxWrapperProps): JSX.Element => {
  if (!hasBackground) {
    return <>{children}</>;
  }

  return (
    <Box
      css={{
        backgroundColor: disabled
          ? theme.checkbox.container.default
          : hasError
          ? theme.checkbox.container.error
          : theme.checkbox.container.default,
      }}
      sx={{
        display: 'inline',
        padding: theme.spacing(3),
        borderRadius: theme.shape.radius8,
      }}
    >
      {children}
    </Box>
  );
};

export const Checkbox = ({
  label,
  checked = false,
  indeterminate = false,
  disabled = false,
  hasError = false,
  inputRef,
  inputProps,
  onChange,
  className = '',
  tabIndex = 0,
  labelSx,
  checkboxSx,
  hasBackground = false,
  ...rest
}: CheckboxProps): JSX.Element => {
  const theme = useTheme();

  const control = (
    <MuiCheckbox
      {...rest}
      icon={hasError ? <CheckboxHasError /> : <CheckboxDefault />}
      checkedIcon={<CheckboxChecked />}
      indeterminateIcon={<CheckboxIndeterminate />}
      indeterminate={indeterminate}
      checked={checked}
      disabled={disabled}
      inputRef={inputRef}
      inputProps={inputProps}
      onChange={onChange}
      className={className}
      tabIndex={tabIndex}
      disableRipple
      sx={checkboxSx}
    />
  );

  if (!Boolean(label)) {
    return (
      <CheckboxWrapper
        theme={theme}
        hasBackground={hasBackground}
        disabled={disabled}
        hasError={hasError}
      >
        {control}
      </CheckboxWrapper>
    );
  }

  return (
    <CheckboxWrapper
      theme={theme}
      hasBackground={hasBackground}
      disabled={disabled}
      hasError={hasError}
    >
      <MuiFormControlLabel
        control={control}
        label={
          typeof label === 'string' ? (
            <BodyS tag='span' color='inherit'>
              {label}
            </BodyS>
          ) : (
            label
          )
        }
        onClick={disabled ? undefined : handleClick}
        checked={checked}
        disabled={disabled}
        sx={{
          marginRight: 0,
          marginLeft: 0,
          color: theme.text.generic.primary,
          ...labelSx,
          [`&.${formControlLabelClasses.root}`]: {
            gap: theme.spacing(2),
          },
          [`.${formControlLabelClasses.label}`]: {
            userSelect: 'none',
            color: 'inherit',
          },
          [`.${checkboxClasses.root}`]: {
            padding: 0,
            margin: '2px',
            alignSelf: 'flex-start',
          },
          [`.${formControlLabelClasses.label}.Mui-disabled`]: {
            color: theme.text.interactive.disabled,
          },
          [`input ~ span`]: {
            backgroundColor:
              !checked && !indeterminate && !hasError
                ? theme.checkbox.background
                : undefined,
          },
        }}
      />
    </CheckboxWrapper>
  );
};
